import { removeIdsFromColumnParameters } from '@pages/TimeLineCreateOrEdit/helpers/removeIdsFromColumnParameters'

import { getDataFromState, getStateFromData } from '@components/CommandParameters/helpers'

import { getPathString } from '@helpers'
import {
  BIND_TYPE,
  TIME_LINE_DEFAULT_TIME_INTERVAL_INCREMENT,
  TIME_LINE_DEFAULT_TIME_INTERVAL_SHOW_FILTER,
  TIME_LINE_MODE,
  TIME_LINE_OBJECT_X_CELL_MIN_HEIGHT,
  TIME_LINE_OBJECT_X_CELL_MIN_WIDTH,
  TYPES_SOURCE_OPTIONS,
} from '@constants'
import {
  ConfiguredColumn,
  MainObjectTypeEnum,
  POSTCreateOrUpdateDTOParams,
  TimeLineDTOType,
  TimeLineGridConfig,
} from '@types'

export const convertFormDataToRequestData = (
  config: TimeLineGridConfig
): POSTCreateOrUpdateDTOParams => {
  return {
    id: config.id,
    code: config.code,
    title: config.title,
    jsonConfig: config.jsonConfig,
    timeInterval:
      config.mode?.id === TIME_LINE_MODE.DATE_TIME
        ? {
            increment: config.timeInterval?.increment ?? TIME_LINE_DEFAULT_TIME_INTERVAL_INCREMENT,
            start: config.timeInterval?.dateRange?.[0],
            end: config.timeInterval?.dateRange?.[1],
            showFilter:
              config.timeInterval?.showFilter ?? TIME_LINE_DEFAULT_TIME_INTERVAL_SHOW_FILTER,
          }
        : undefined,
    objectY: {
      keyField: [
        {
          field: config.objectY.keyField?.id as string,
          objectCode: config.objectY.object?.id as string,
        },
      ],
      columns: config.objectY.columns.map((item, index) => {
        return {
          ...item,
          order: index,
          parameters: removeIdsFromColumnParameters(item.parameters),
        }
      }) as ConfiguredColumn[],
      objectCode: config.objectY.object?.id as string,
      getDataCommand: {
        internalId: '',
        name: config.objectY.getDataCommand?.name?.id as string,
        httpMethod: config.objectY.getDataCommand?.name?.httpMethod as string,
        resource: TYPES_SOURCE_OPTIONS.OBJECT,
        objectCode: config.objectY.object?.id as string,
        objectTitle: config.objectY.object?.label as string,
        parameters: getDataFromState(config.objectY.getDataCommand?.parameters),
        body: getDataFromState(config.objectY.getDataCommand?.body),
      },
      preFilter: config.objectY.preFilter as string,
      actions: config.objectY?.actions?.map(item => {
        return {
          ...item,
          tooltipId: item.tooltipId || item.objectTooltipId,
        }
      }),
    },
    objectX: {
      startDateField: config.objectX.startDateField?.id as string,
      endDateField: config.objectX.endDateField?.id as string,
      objectCode: config.objectX.object?.id as string,
      getDataCommand: {
        internalId: '',
        name: config.objectX.getDataCommand?.name?.id as string,
        httpMethod: config.objectX.getDataCommand?.name?.httpMethod as string,
        resource: TYPES_SOURCE_OPTIONS.OBJECT,
        objectCode: config.objectX.object?.id as string,
        objectTitle: config.objectX.object?.label as string,
        parameters: getDataFromState(config.objectX.getDataCommand?.parameters),
        body: getDataFromState(config.objectX.getDataCommand?.body),
      },
      linkToObjectY:
        config.objectX.linkToObjectY?.pathArray?.filter(item => item.field) || undefined,
      linkToColumnsObject:
        config.objectX.linkToColumnsObject?.pathArray?.filter(item => item.field) || undefined,
      fieldFromColumnsObjectToLink:
        config.objectX.fieldFromColumnsObjectToLink?.pathArray?.filter(item => item.field) || [],
      fieldFromObjectYToLink:
        config.objectX.fieldFromObjectYToLink?.pathArray?.filter(item => item.field) || [],
      columnsObject: config.objectX.columnsObject?.object?.id
        ? {
            objectCode: config.objectX.columnsObject.object.id as string,
            objectTitle: config.objectX.columnsObject.object.label as string,
            command: {
              internalId: '',
              name: config.objectX.columnsObject.command?.name?.id as string,
              httpMethod: config.objectX.columnsObject.command?.name?.httpMethod as string,
              resource: TYPES_SOURCE_OPTIONS.OBJECT,
              objectCode: config.objectX.columnsObject.object.id as string,
              objectTitle: config.objectX.columnsObject.object.label as string,
              parameters: getDataFromState(config.objectX.columnsObject.command?.parameters),
              body: getDataFromState(config.objectX.columnsObject.command?.body),
            },
            headerBindType: config.objectX.columnsObject?.headerBindType?.id as BIND_TYPE,
            headerField: config.objectX.columnsObject?.headerField?.pathArray?.filter(
              item => item.field
            ),
            headerScript: config.objectX.columnsObject?.headerScript ?? '',
          }
        : undefined,
      cell: {
        bindType: config.objectX.cell?.bindType?.id as BIND_TYPE,
        value: config.objectX.cell?.value,
        minWidth: config.objectX.cell?.minWidth,
        minHeight: config.objectX.cell?.minHeight,
      },
      columns: config.objectX.columns.map((item, index) => {
        return {
          ...item,
          order: index,
          parameters: removeIdsFromColumnParameters(item.parameters),
        }
      }) as ConfiguredColumn[],
      actions: config.objectX.actions.map(({ contextMenu, ...item }) => {
        return {
          ...item,
          contextMenuId: item.contextMenuId || contextMenu?.id,
          tooltipId: item.tooltipId || item.objectTooltipId,
          variableId: item.variableId || item?.variable?.id,
        }
      }),
      totalsRows: config.objectX.totalsRows?.map(row => {
        return {
          ...row,
          commands: row?.commands.map(command => {
            return {
              internalId: '',
              name: command.name?.id as string,
              httpMethod: command?.name?.httpMethod as string,
              resource: command.sourceType?.id,
              objectCode: command?.sourceCommand?.id as string,
              objectTitle: command?.sourceCommand?.label as string,
              parameters: getDataFromState(command?.parameters),
              type: command.type?.id,
              body: getDataFromState(command?.body),
            }
          }),
          bindType: row.bindType?.id as string,
        }
      }),
    },
    showUpdateTimeLineDataButton: config.showUpdateTimeLineDataButton,
    showDayOfWeek: config.showDayOfWeek,
    mainObject: {
      objectType: config.mainObject?.objectType,
      groupTitleScript: config.mainObject?.groupTitleScript,
      groupTitleType: config.mainObject?.groupTitleType?.id,
      groupField: config.mainObject?.groupField?.filter(item => item.field) || null,
      groupTitleDisplayField:
        config.mainObject?.groupTitleDisplayField?.filter(item => item.field) || [],
    },
    actions: config.actions.map(item => {
      return {
        ...item,
        variableId: item.variableId || item?.variable?.id,
      }
    }),
    searchAssistantId: config.searchAssistant?.id,
    filterColumnsObjectByObjectX: config.filterColumnsObjectByObjectX,
    filterObjectYByObjectX: config.filterObjectYByObjectX,
    mode: config.mode?.id,
  }
}

export const convertResponseDataToFormData = (config: TimeLineDTOType): TimeLineGridConfig => {
  return {
    ...config,
    timeInterval: config.timeInterval
      ? {
          increment: config.timeInterval.increment,
          dateRange: [config.timeInterval.start || '', config.timeInterval.end || ''],
          showFilter: config.timeInterval.showFilter,
        }
      : undefined,
    objectY: {
      ...config.objectY,
      columns: config.objectY.columns,
      object: {
        id: config.objectY.objectCode as string,
        label: config.objectY.objectTitle as string,
      },
      keyField: {
        id: getPathString(config.objectY.keyField || []),
        label: getPathString(config.objectY.keyField || []),
      },
      getDataCommand: {
        ...config.objectY.getDataCommand,
        internalId: config.objectY.getDataCommand?.internalId as string,
        name: {
          id: config.objectY.getDataCommand?.name as string,
          label: config.objectY.getDataCommand?.name as string,
          httpMethod: config.objectY.getDataCommand?.httpMethod,
        },
        objectCode: {
          id: config.objectY.getDataCommand?.objectCode as string,
          label: config.objectY.getDataCommand?.objectTitle as string,
        },
        type: {
          id: config.objectY.getDataCommand?.type as string,
          label: config.objectY.getDataCommand?.type as string,
        },
        parameters: getStateFromData(config.objectY.getDataCommand?.parameters),
        body: getStateFromData(config.objectY.getDataCommand?.body),
      },
      actions: config.objectY.actions || [],
    },
    objectX: {
      ...config.objectX,
      cell: {
        ...config.objectX.cell,
        bindType: { id: config.objectX.cell.bindType, label: config.objectX.cell.bindType },
        minWidth: config.objectX.cell?.minWidth ?? TIME_LINE_OBJECT_X_CELL_MIN_WIDTH,
        minHeight: config.objectX.cell?.minHeight ?? TIME_LINE_OBJECT_X_CELL_MIN_HEIGHT,
      },
      linkToObjectY: config.objectX.linkToObjectY
        ? {
            keyField: getPathString(config.objectX.linkToObjectY),
            pathArray: config.objectX.linkToObjectY || [],
          }
        : undefined,
      linkToColumnsObject: config.objectX.linkToColumnsObject
        ? {
            keyField: getPathString(config.objectX.linkToColumnsObject),
            pathArray: config.objectX.linkToColumnsObject || [],
          }
        : { keyField: null, pathArray: [] },
      fieldFromColumnsObjectToLink: config.objectX.fieldFromColumnsObjectToLink
        ? {
            keyField: getPathString(config.objectX.fieldFromColumnsObjectToLink),
            pathArray: config.objectX.fieldFromColumnsObjectToLink || [],
          }
        : { keyField: null, pathArray: [] },
      fieldFromObjectYToLink: config.objectX.fieldFromObjectYToLink
        ? {
            keyField: getPathString(config.objectX.fieldFromObjectYToLink),
            pathArray: config.objectX.fieldFromObjectYToLink || [],
          }
        : { keyField: null, pathArray: [] },
      columnsObject: config.objectX.columnsObject
        ? {
            object: {
              id: config.objectX.columnsObject.objectCode as string,
              label: config.objectX.columnsObject.objectTitle as string,
            },
            command: {
              ...config.objectX.columnsObject.command,
              internalId: config.objectX.columnsObject.command?.internalId as string,
              name: {
                id: config.objectX.columnsObject.command?.name as string,
                label: config.objectX.columnsObject.command?.name as string,
                httpMethod: config.objectX.columnsObject.command?.httpMethod,
              },
              objectCode: {
                id: config.objectX.columnsObject.command?.objectCode as string,
                label: config.objectX.columnsObject.command?.objectTitle as string,
              },
              type: config.objectX.columnsObject.command?.type
                ? {
                    id: config.objectX.columnsObject.command.type as string,
                    label: config.objectX.columnsObject.command.type as string,
                  }
                : null,
              parameters: getStateFromData(config.objectX.columnsObject.command?.parameters),
              body: getStateFromData(config.objectX.columnsObject.command?.body),
            },
            headerBindType: config.objectX.columnsObject?.headerBindType
              ? {
                  id: config.objectX.columnsObject.headerBindType as string,
                  label: config.objectX.columnsObject.headerBindType as string,
                }
              : null,
            headerField: config.objectX.columnsObject?.headerField?.length
              ? {
                  keyField: getPathString(config.objectX.columnsObject.headerField),
                  pathArray: config.objectX.columnsObject.headerField,
                }
              : { keyField: null, pathArray: [] },
            headerScript: config.objectX.columnsObject?.headerScript ?? '',
          }
        : {
            object: undefined,
            command: {
              internalId: '',
              type: null,
              name: null,
              parameters: [],
              body: [],
            },
            headerBindType: null,
            headerField: { keyField: null, pathArray: [] },
            headerScript: '',
          },
      object: {
        id: config.objectX.objectCode as string,
        label: config.objectX.objectTitle as string,
      },
      startDateField: config.objectX.startDateField
        ? {
            id: config.objectX.startDateField,
            label: config.objectX.startDateField,
          }
        : null,
      endDateField: config.objectX.endDateField
        ? {
            id: config.objectX.endDateField,
            label: config.objectX.endDateField,
          }
        : null,
      totalsRows:
        config.objectX.totalsRows?.map(row => {
          return {
            ...row,
            commands: row.commands.map(command => {
              return {
                ...command,
                internalId: command?.internalId as string,
                name: {
                  id: command?.name as string,
                  label: command?.name as string,
                  httpMethod: command?.httpMethod,
                },
                objectCode: {
                  id: command?.objectCode as string,
                  label: command?.objectTitle as string,
                },
                type: {
                  id: command?.type as string,
                  label: command?.type as string,
                },
                sourceCommand: {
                  id: command.objectCode,
                  label: command.objectTitle,
                  code: command.objectCode,
                },
                sourceType: {
                  id: command.resource as TYPES_SOURCE_OPTIONS,
                  label: command.resource as string,
                },
                parameters: getStateFromData(command?.parameters),
                body: getStateFromData(command?.body),
              }
            }),
            bindType: {
              id: row.bindType as BIND_TYPE,
              label: row.bindType,
            },
          }
        }) || [],
      getDataCommand: {
        ...config.objectX.getDataCommand,
        internalId: config.objectX.getDataCommand?.internalId as string,
        name: {
          id: config.objectX.getDataCommand?.name as string,
          label: config.objectX.getDataCommand?.name as string,
          httpMethod: config.objectX.getDataCommand?.httpMethod,
        },
        objectCode: {
          id: config.objectX.getDataCommand?.objectCode as string,
          label: config.objectX.getDataCommand?.objectTitle as string,
        },
        type: {
          id: config.objectX.getDataCommand?.type as string,
          label: config.objectX.getDataCommand?.type as string,
        },
        parameters: getStateFromData(config.objectX.getDataCommand?.parameters),
        body: getStateFromData(config.objectX.getDataCommand?.body),
      },
      actions: config.objectX.actions || [],
    },
    mainObject: {
      objectType: config.mainObject?.objectType || MainObjectTypeEnum.Y,
      groupTitleType: config.mainObject?.groupTitleType
        ? {
            label: config.mainObject?.groupTitleType,
            id: config.mainObject?.groupTitleType,
          }
        : null,
      groupTitleScript: config.mainObject?.groupTitleScript,
      groupField: config.mainObject?.groupField,
      groupFieldKey: config.mainObject?.groupField
        ? getPathString(config.mainObject.groupField)
        : '',
      groupTitleDisplayField: config.mainObject?.groupTitleDisplayField,
      groupTitleDisplayFieldKey: config.mainObject?.groupTitleDisplayField
        ? getPathString(config.mainObject.groupTitleDisplayField)
        : '',
    },
    actions: config.actions || [],
    variables: config.variables || [],
    showDayOfWeek: config.showDayOfWeek || false,
    searchAssistant:
      config.searchAssistantId && config.searchAssistantTitle
        ? {
            id: config.searchAssistantId,
            label: config.searchAssistantTitle,
          }
        : null,
    filterColumnsObjectByObjectX: config.filterColumnsObjectByObjectX || false,
    filterObjectYByObjectX: config.filterObjectYByObjectX || false,
    mode: {
      id: config.mode || TIME_LINE_MODE.DATE_TIME,
      label: config.mode || TIME_LINE_MODE.DATE_TIME,
    },
  }
}
